import { EV } from '../core/eventbus.js';

/**
 * 书架：把本地文件收进屋里，慢慢读。
 * 支持 TXT / Markdown / CSV / HTML / PDF / Word(docx) / Excel(xlsx,xls)。
 * 书上传到服务器（同源的 /api/books），换台设备、换个浏览器都还在。
 */

const API = '/api/books';                  // 同源后端：Pages 的 _worker.js
const LIB_TOKEN = 'dongtianju-2026';       // 与 worker/wrangler.toml 里的 LIB_TOKEN 保持一致
const FONT_KEY = 'dongtianju.reader.fontSize';
const PROGRESS_KEY = 'dongtianju.reader.progress';

const KIND_BY_EXT = {
  txt: 'text', text: 'text', log: 'text',
  md: 'md', markdown: 'md',
  csv: 'csv',
  html: 'html', htm: 'html', mhtml: 'html',
  pdf: 'pdf',
  docx: 'docx', doc: 'doc',
  xlsx: 'xlsx', xls: 'xls',
};

const EXT_OF_KIND = {
  text: '.txt', md: '.md', csv: '.csv', html: '.html',
  pdf: '.pdf', docx: '.docx', xlsx: '.xlsx', xls: '.xls',
};

// 解析 Word / Excel 要借两个现成的库，按需从 CDN 取，用不上就不下载
const CDN = {
  mammoth: [
    'https://cdn.jsdelivr.net/npm/mammoth@1.8.0/mammoth.browser.min.js',
    'https://unpkg.com/mammoth@1.8.0/mammoth.browser.min.js',
  ],
  xlsx: [
    'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js',
    'https://unpkg.com/xlsx@0.18.5/dist/xlsx.full.min.js',
  ],
};

/* ---------------- 小工具 ---------------- */

function extOf(name) {
  const m = /\.([a-z0-9]+)$/i.exec(String(name || ''));
  return m ? m[1].toLowerCase() : '';
}
export function kindOf(name) {
  return KIND_BY_EXT[extOf(name)] || null;
}
function baseName(name) {
  return String(name || '').replace(/\.[^.]+$/, '') || '无题';
}
function fmtSize(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
}
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

/** 文本解码：优先 UTF-8，失败（多半是 GBK 的中文书）再退回 GBK */
export function decodeText(buf) {
  const u8 = new Uint8Array(buf);
  if (u8[0] === 0xef && u8[1] === 0xbb && u8[2] === 0xbf) return new TextDecoder('utf-8').decode(u8.subarray(3));
  if (u8[0] === 0xff && u8[1] === 0xfe) return new TextDecoder('utf-16le').decode(u8);
  if (u8[0] === 0xfe && u8[1] === 0xff) return new TextDecoder('utf-16be').decode(u8);
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(u8);
  } catch (e) {
    try { return new TextDecoder('gbk').decode(u8); } catch (e2) { return new TextDecoder('utf-8').decode(u8); }
  }
}

/** 极简 Markdown：只做标题、引用、列表、分割线、行内强调，够看书就行 */
export function renderMarkdown(src) {
  const lines = String(src || '').replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let para = [];
  let inList = false;

  const inline = (s) => escapeHtml(s)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<i>$2</i>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  const flushPara = () => {
    if (para.length) { out.push('<p>' + inline(para.join(' ')) + '</p>'); para = []; }
  };
  const closeList = () => { if (inList) { out.push('</ul>'); inList = false; } };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushPara(); closeList(); continue; }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) { flushPara(); closeList(); out.push(`<h${Math.min(6, h[1].length)}>${inline(h[2])}</h${Math.min(6, h[1].length)}>`); continue; }
    if (/^(-{3,}|\*{3,})$/.test(line.trim())) { flushPara(); closeList(); out.push('<hr>'); continue; }
    if (/^>\s?/.test(line)) { flushPara(); closeList(); out.push('<blockquote>' + inline(line.replace(/^>\s?/, '')) + '</blockquote>'); continue; }
    const li = /^[-*+]\s+(.*)$/.exec(line);
    if (li) { flushPara(); if (!inList) { out.push('<ul>'); inList = true; } out.push('<li>' + inline(li[1]) + '</li>'); continue; }
    closeList();
    para.push(line);
  }
  flushPara();
  closeList();
  return out.join('\n');
}

/** CSV 解析成表格（支持引号包裹与转义） */
export function csvToHtml(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  const src = String(text || '').replace(/\r\n?/g, '\n');
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; } else quoted = false;
      } else cell += c;
      continue;
    }
    if (c === '"') { quoted = true; continue; }
    if (c === ',') { row.push(cell); cell = ''; continue; }
    if (c === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; continue; }
    cell += c;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  if (!rows.length) return '<p class="tips">空表。</p>';
  const head = rows[0];
  const body = rows.slice(1);
  let html = '<table><thead><tr>';
  for (const h of head) html += `<th>${escapeHtml(h)}</th>`;
  html += '</tr></thead><tbody>';
  for (const r of body) {
    html += '<tr>';
    for (const v of r) html += `<td>${escapeHtml(v)}</td>`;
    html += '</tr>';
  }
  return html + '</tbody></table>';
}

/** 清掉脚本与危险属性，外来 HTML 才能安全显示 */
export function sanitizeHtml(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  for (const el of doc.querySelectorAll('script,style,iframe,object,embed,link,meta')) el.remove();
  for (const el of doc.querySelectorAll('*')) {
    for (const attr of [...el.attributes]) {
      const n = attr.name.toLowerCase();
      if (n.startsWith('on')) el.removeAttribute(attr.name);
      else if ((n === 'href' || n === 'src') && /^\s*javascript:/i.test(attr.value)) el.removeAttribute(attr.name);
    }
  }
  return doc.body.innerHTML;
}

/** 给外来 HTML 补上阅读区排版；没有样式表时也能读 */
const READER_CSS = '<link rel="stylesheet" href="./fonts/lxgw/style.css">' + '<style>'
  + 'body{font-family:"LXGW WenKai Lite","Kaiti SC","STKaiti","KaiTi","Songti SC",serif;'
  + 'font-size:16px;line-height:1.9;color:#3b3733;padding:14px 16px;margin:0;word-break:break-word;}'
  + 'img{max-width:100%;height:auto;}pre{white-space:pre-wrap;}table{border-collapse:collapse;}'
  + 'td,th{border:1px solid #cdbfa4;padding:3px 6px;}'
  + '</style>';

function loadScript(urls) {
  return new Promise((resolve, reject) => {
    let i = 0;
    const next = () => {
      if (i >= urls.length) return reject(new Error('解析库加载失败'));
      const s = document.createElement('script');
      s.src = urls[i++];
      s.onload = () => resolve();
      s.onerror = () => { s.remove(); next(); };
      document.head.appendChild(s);
    };
    next();
  });
}
function globalReady(name, urls, timeout = 15000) {
  // 库已在页面里就直接用，否则下载并等它挂到 window 上
  if (typeof window !== 'undefined' && window[name]) return Promise.resolve(window[name]);
  return loadScript(urls).then(() => new Promise((resolve, reject) => {
    const t0 = Date.now();
    const tick = () => {
      if (window[name]) return resolve(window[name]);
      if (Date.now() - t0 > timeout) return reject(new Error('解析库迟迟没就绪'));
      setTimeout(tick, 60);
    };
    tick();
  }));
}

/* ---------------- 与书库后端通信 ---------------- */

async function api(path = '', opts = {}) {
  const res = await fetch(API + path, opts);
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j && j.error) msg = j.error;
    } catch (e) { /* 不是 JSON 就用状态码 */ }
    throw new Error(msg);
  }
  return res;
}
function writeHeaders(extra = {}) { return { 'x-token': LIB_TOKEN, ...extra }; }

/**
 * 屋里常备的书：跟屋子一起发货，存在站内页面里，既不占服务器也不占书库额度。
 * file 是相对页面地址的路径；builtin 为真时读写都走本地，不走 /api/books。
 */
export const BUILTIN_BOOKS = [
  { id: 'builtin:guide', name: '洞天居·全玩法攻略', kind: 'html', file: 'books/guide.html', size: 0, added: Infinity, builtin: true },
];

/* ---------------- 面板 ---------------- */

export class BookshelfPanel {
  constructor(engine) {
    this.engine = engine;
    this.el = document.getElementById('shelf');
    this.listEl = document.getElementById('shelf-list');
    this.listView = document.getElementById('shelf-list-view');
    this.readView = document.getElementById('shelf-read-view');
    this.bodyEl = document.getElementById('shelf-body');
    this.titleEl = document.getElementById('shelf-title');
    this.countEl = document.getElementById('shelf-count');
    this.fileInput = document.getElementById('shelf-file');

    this.books = [];
    this.current = null;
    this.blobUrl = null;
    this.offline = false;
    this.fontSize = this.loadFont();

    if (!this.el) return;   // 冒烟测试里没有 DOM，安静退场
    this.applyFont();
    this.bind();
  }

  bind() {
    const add = document.getElementById('shelf-add');
    const back = document.getElementById('shelf-back');
    const bigger = document.getElementById('shelf-bigger');
    const smaller = document.getElementById('shelf-smaller');
    const download = document.getElementById('shelf-download');
    const del = document.getElementById('shelf-delete');
    const closeBtn = this.el.querySelector('[data-close]');

    if (add && this.fileInput) add.onclick = () => this.fileInput.click();
    if (this.fileInput) this.fileInput.onchange = (e) => { this.addFiles(e.target.files); e.target.value = ''; };
    if (back) back.onclick = () => this.showList();
    if (bigger) bigger.onclick = () => this.setFont(this.fontSize + 1);
    if (smaller) smaller.onclick = () => this.setFont(this.fontSize - 1);
    if (download) download.onclick = () => this.downloadCurrent();
    if (del) del.onclick = () => this.deleteCurrent();
    if (closeBtn) closeBtn.onclick = () => this.close();

    // 阅读进度：滚动停下 400ms 后记一次
    if (this.bodyEl) {
      let timer = null;
      this.bodyEl.addEventListener('scroll', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.saveProgress(), 400);
      }, { passive: true });
    }

    this.engine.events.on(EV.SHELF_OPEN, () => this.open());
  }

  toast(text, kind = 'info') { this.engine.events.emit(EV.TOAST, { text, kind }); }

  /* ---- 视图切换 ---- */
  open() {
    if (!this.el) return;
    this.el.classList.remove('hidden');
    this.showList();
    this.refresh();
  }

  close() {
    if (!this.el) return;
    this.saveProgress();
    this.el.classList.add('hidden');
    this.releaseUrl();
    if (this.bodyEl) this.bodyEl.innerHTML = '';
  }

  isOpen() { return !!this.el && !this.el.classList.contains('hidden'); }

  showList() {
    this.saveProgress();
    this.releaseUrl();
    this.current = null;
    if (this.bodyEl) this.bodyEl.innerHTML = '';
    if (this.listView) this.listView.classList.remove('hidden');
    if (this.readView) this.readView.classList.add('hidden');
  }

  showRead() {
    if (this.listView) this.listView.classList.add('hidden');
    if (this.readView) this.readView.classList.remove('hidden');
  }

  /* ---- 字号 ---- */
  loadFont() {
    try { return Math.max(12, Math.min(28, parseInt(localStorage.getItem(FONT_KEY) || '16', 10) || 16)); }
    catch (e) { return 16; }
  }
  setFont(size) {
    this.fontSize = Math.max(12, Math.min(28, size));
    try { localStorage.setItem(FONT_KEY, String(this.fontSize)); } catch (e) { /* 无痕模式忽略 */ }
    this.applyFont();
  }
  applyFont() {
    if (this.bodyEl) this.bodyEl.style.setProperty('--reader-fs', this.fontSize + 'px');
  }

  /* ---- 书库 ---- */
  offlineBooks() {
    return BUILTIN_BOOKS.map((b) => ({ ...b }));
  }

  async refresh() {
    let server = [];
    this.offline = false;
    try {
      const res = await api('');
      const data = await res.json();
      server = (data.books || []).sort((a, b) => (b.added || 0) - (a.added || 0));
    } catch (err) {
      // 接不上网上的书库（本地预览、断网）也别慌，屋里的常备书照样能看
      this.offline = true;
      this.offlineMsg = err.message;
    }
    this.books = [...this.offlineBooks(), ...server];
    if (this.offline && server.length === 0 && this.el) {
      this.toast('网上的书库还没连上，先看着屋里常备的这本。', 'info');
    }
    this.renderList();
  }

  /** 取一本书的字节：常备书走本地文件，其余走书库接口 */
  async bufOf(rec) {
    if (rec.builtin) {
      const res = await fetch(new URL(rec.file, location.href));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.arrayBuffer();
    }
    const res = await api('/' + rec.id);
    return await (await res.blob()).arrayBuffer();
  }

  renderList() {
    if (!this.listEl) return;
    if (this.countEl) this.countEl.textContent = `共 ${this.books.length} 本`;
    this.listEl.innerHTML = '';
    if (!this.books.length) {
      this.listEl.innerHTML = '<p class="tips">架上还空着。点「添书」把 TXT、PDF、Word、Excel、HTML 收进来。</p>';
      return;
    }
    for (const rec of this.books) {
      const card = document.createElement('div');
      card.className = 'book';
      const size = rec.builtin ? '屋里常备' : fmtSize(rec.size);
      card.title = rec.builtin ? `${rec.file}　（屋里常备）` : `${rec.file}　${fmtSize(rec.size)}`;
      const kindTag = { text: '文', md: '文', csv: '表', html: '页', pdf: 'PDF', docx: 'Word', xlsx: '表', xls: '表' }[rec.kind] || '书';
      card.innerHTML = `<b>${escapeHtml(rec.name)}</b><span class="kind">${kindTag}</span>`
        + `<span class="muted">${size}</span>`;
      card.onclick = () => this.read(rec);
      this.listEl.appendChild(card);
    }
    if (this.offline) {
      const p = document.createElement('p');
      p.className = 'tips';
      p.textContent = '网上的书库还没连上（本地预览或断网），眼下这一本是屋里自带的。';
      this.listEl.appendChild(p);
    }
  }

  async addFiles(fileList) {
    const files = [...(fileList || [])];
    if (!files.length) return;
    let ok = 0;
    for (const f of files) {
      const kind = kindOf(f.name);
      if (!kind) { this.toast(`《${baseName(f.name)}》的格式还认不出来。`, 'warn'); continue; }
      if (kind === 'doc') {
        this.toast('老版 .doc 读不了，请在 Word 里另存为 .docx。', 'warn');
        continue;
      }
      const title = baseName(f.name);
      try {
        this.toast(`正在上传《${title}》…`, 'info');
        await api('', {
          method: 'POST',
          headers: writeHeaders({
            'x-book-name': encodeURIComponent(title),
            'x-book-kind': kind,
          }),
          body: f,
        });
        ok++;
      } catch (err) {
        this.toast(`《${title}》没能上架：${err.message}`, 'warn');
      }
    }
    if (ok) this.toast(`收进 ${ok} 本书。`, 'good');
    await this.refresh();
  }

  /* ---- 阅读 ---- */
  async read(rec) {
    this.current = rec;
    this.showRead();
    if (this.titleEl) this.titleEl.textContent = rec.name;
    if (this.bodyEl) {
      this.bodyEl.className = '';
      this.bodyEl.innerHTML = '<p class="tips">正在拆开…</p>';
    }
    try {
      const buf = await this.bufOf(rec);
      const html = await this.toHtml(rec, buf);
      if (!this.bodyEl || this.current !== rec) return;   // 中途翻走了就算了
      this.releaseUrl();
      if (html.kind === 'url') {
        this.blobUrl = html.url;
        const f = document.createElement('iframe');
        f.className = 'reader-frame';
        f.src = html.url;
        if (html.sandbox) f.setAttribute('sandbox', html.sandbox);
        this.bodyEl.className = 'frame';
        this.bodyEl.innerHTML = '';
        this.bodyEl.appendChild(f);
      } else if (html.kind === 'srcdoc') {
        const f = document.createElement('iframe');
        f.className = 'reader-frame';
        f.setAttribute('sandbox', 'allow-scripts allow-popups');
        f.srcdoc = html.html;
        this.bodyEl.className = 'frame';
        this.bodyEl.innerHTML = '';
        this.bodyEl.appendChild(f);
        if (html.note) this.appendNote(html.note);
      } else {
        this.bodyEl.className = 'text';
        this.bodyEl.innerHTML = html.html;
        this.restoreProgress(rec);
      }
    } catch (err) {
      if (this.bodyEl) this.bodyEl.innerHTML = `<p class="tips">这本书读不出来：${escapeHtml(err.message || err)}</p>`;
    }
  }

  appendNote(text) {
    if (!this.bodyEl || !text) return;
    const p = document.createElement('p');
    p.className = 'tips';
    p.textContent = text;
    this.bodyEl.appendChild(p);
  }

  async toHtml(rec, buf) {
    switch (rec.kind) {
      case 'text': {
        const text = decodeText(buf);
        return { kind: 'html', html: `<div class="plain">${escapeHtml(text)}</div>` };
      }
      case 'md':
        return { kind: 'html', html: renderMarkdown(decodeText(buf)) };
      case 'csv':
        return { kind: 'html', html: csvToHtml(decodeText(buf)) };
      case 'html': {
        const raw = decodeText(buf);
        const hasHead = /<html[\s>]/i.test(raw);
        const doc = hasHead ? raw : `<!DOCTYPE html><html><head><meta charset="utf-8">${READER_CSS}</head><body>${raw}</body></html>`;
        return { kind: 'srcdoc', html: doc, note: '这是网页，按原样显示。' };
      }
      case 'pdf': {
        const url = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
        return { kind: 'url', url };
      }
      case 'docx': {
        const mammoth = await globalReady('mammoth', CDN.mammoth);
        const res = await mammoth.convertToHtml({ arrayBuffer: buf });
        return { kind: 'html', html: sanitizeHtml(res.value || '<p class="tips">空的。</p>') };
      }
      case 'xlsx':
      case 'xls': {
        const XLSX = await globalReady('XLSX', CDN.xlsx);
        const wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
        const names = wb.SheetNames || [];
        if (!names.length) return { kind: 'html', html: '<p class="tips">空表。</p>' };
        let html = '';
        for (const n of names) {
          const sheetHtml = XLSX.utils.sheet_to_html(wb.Sheets[n]);
          html += names.length > 1
            ? `<h3 class="sheet-name">${escapeHtml(n)}</h3>${sanitizeHtml(sheetHtml)}`
            : sanitizeHtml(sheetHtml);
        }
        return { kind: 'html', html };
      }
      default:
        throw new Error('不支持的格式');
    }
  }

  /* ---- 进度：按书号记在本机，换台设备不跟着走 ---- */
  progressMap() {
    try { return JSON.parse(localStorage.getItem(PROGRESS_KEY) || '{}'); } catch (e) { return {}; }
  }

  saveProgress() {
    const rec = this.current;
    if (!rec || !this.bodyEl) return;
    if (rec.kind === 'pdf' || rec.kind === 'html') return;   // 内嵌在 iframe 里，读不到滚动位置
    const el = this.bodyEl;
    const max = el.scrollHeight - el.clientHeight;
    if (max <= 0) return;
    const p = Math.max(0, Math.min(1, el.scrollTop / max));
    const map = this.progressMap();
    if (Math.abs((map[rec.id] || 0) - p) < 0.005) return;
    map[rec.id] = p;
    try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(map)); } catch (e) { /* 无痕模式忽略 */ }
  }

  restoreProgress(rec) {
    if (!this.bodyEl) return;
    const p = this.progressMap()[rec.id] || 0;
    if (!p) return;
    const el = this.bodyEl;
    const max = el.scrollHeight - el.clientHeight;
    if (max > 0) el.scrollTop = p * max;
  }

  /* ---- 下载 / 删除 ---- */
  async downloadCurrent() {
    const rec = this.current;
    if (!rec) return;
    try {
      const buf = await this.bufOf(rec);
      const url = URL.createObjectURL(new Blob([buf]));
      const a = document.createElement('a');
      a.href = url;
      a.download = rec.name + (EXT_OF_KIND[rec.kind] || '');
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) {
      this.toast(`下载失败：${err.message}`, 'warn');
    }
  }

  async deleteCurrent() {
    const rec = this.current;
    if (!rec) return;
    if (rec.builtin) { this.toast('这是屋里常备的书，撤不下来。', 'info'); return; }
    if (!window.confirm(`把《${rec.name}》从架上撤下来？服务器上也会删掉。`)) return;
    try {
      await api('/' + rec.id, { method: 'DELETE', headers: writeHeaders() });
    } catch (err) {
      this.toast('没撤下来：' + err.message, 'warn');
      return;
    }
    this.toast(`《${rec.name}》已撤下。`, 'info');
    this.books = this.books.filter((b) => b.id !== rec.id);   // KV 同步有延迟，先本地撤掉
    this.renderList();
    this.showList();
    this.refresh();
  }

  releaseUrl() {
    if (this.blobUrl) { URL.revokeObjectURL(this.blobUrl); this.blobUrl = null; }
  }
}
